/**
 * Google Analytics 4 (GA4) Service
 *
 * Provides type-safe methods for tracking page views and custom events.
 * Respects user privacy preferences (Do Not Track) and environment configuration.
 * Dynamically loads the gtag.js script based on environment configuration.
 *
 * @see https://developers.google.com/analytics/devguides/collection/ga4
 */

import { config } from '@/config';
import type {
  GtagCommand,
  GtagConfigParams,
  GtagEventParams,
} from '@/types/window';

// Re-export types for consumers
export interface PageViewParams {
  path: string;
  title?: string;
  location?: string;
}

export interface EventParams {
  category?: string;
  label?: string;
  value?: number;
  [key: string]: unknown;
}

// Track initialization state
let isScriptLoaded = false;
let isScriptLoading = false;
let initializationPromise: Promise<void> | null = null;
const SENSITIVE_PAGE_QUERY_PARAMS = new Set(['email', 'token']);

const buildCurrentUrl = (): URL | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return new URL(window.location.href);
};

const sanitizePageUrl = (value: string): string => {
  const currentUrl = buildCurrentUrl();
  const fallbackBase = currentUrl?.origin ?? 'https://terramatrix.invalid';
  const isAbsoluteUrl = /^[a-z][a-z\d+.-]*:/i.test(value);
  const parsedUrl = new URL(value, fallbackBase);

  SENSITIVE_PAGE_QUERY_PARAMS.forEach((queryParam) => {
    parsedUrl.searchParams.delete(queryParam);
  });

  return isAbsoluteUrl
    ? parsedUrl.toString()
    : `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`;
};

const getDefaultPageLocation = (): string => {
  const currentUrl = buildCurrentUrl();
  return currentUrl ? sanitizePageUrl(currentUrl.toString()) : '';
};

/**
 * Check if analytics should be enabled based on configuration and user preferences
 */
const shouldEnableAnalytics = (): boolean => {
  // Check if analytics is enabled in config
  if (!config.analytics.enabled) {
    return false;
  }

  // Check for valid measurement ID
  if (!config.analytics.measurementId || config.analytics.measurementId === 'G-XXXXXXXXXX') {
    return false;
  }

  // Respect Do Not Track browser setting if configured
  if (config.analytics.respectDoNotTrack) {
    const browserDoNotTrack = Reflect.get(window, 'doNotTrack');
    const dnt =
      navigator.doNotTrack ||
      (typeof browserDoNotTrack === 'string' ? browserDoNotTrack : undefined);
    if (dnt === '1' || dnt === 'yes') {
      return false;
    }
  }

  return true;
};

/**
 * Check if gtag is available on the window object
 */
const isGtagAvailable = (): boolean => {
  return typeof window !== 'undefined' && typeof window.gtag === 'function';
};

/**
 * Load the gtag.js script dynamically
 */
const loadGtagScript = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Window not available'));
      return;
    }

    const measurementId = config.analytics.measurementId;

    // Create and append the gtag.js script
    const script = document.createElement('script');
    script.async = true;
    script.referrerPolicy = 'no-referrer';
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

    script.onload = () => {
      // Initialize dataLayer and gtag function
      window.dataLayer = window.dataLayer || [];
      function gtag(command: 'js', date: Date): void;
      function gtag(
        command: 'config',
        targetId: string,
        configParams?: GtagConfigParams,
      ): void;
      function gtag(
        command: 'event',
        eventName: string,
        eventParams?: GtagEventParams,
      ): void;
      function gtag(command: 'set', params: Record<string, unknown>): void;
      function gtag(
        command: 'consent',
        consentArg: string,
        params: Record<string, string>,
      ): void;
      function gtag(...args: [GtagCommand, ...unknown[]]): void {
        window.dataLayer?.push(args);
      }

      window.gtag = gtag;
      gtag('js', new Date());

      isScriptLoaded = true;
      isScriptLoading = false;

      resolve();
    };

    script.onerror = () => {
      isScriptLoading = false;
      const error = new Error('Failed to load Google Analytics script');
      if (config.analytics.debug) {
        console.error('[GA4]', error.message);
      }
      reject(error);
    };

    document.head.appendChild(script);
  });
};

/**
 * Initialize Google Analytics
 * Loads the script dynamically and configures GA4
 */
export const initializeGA = async (): Promise<void> => {
  if (!shouldEnableAnalytics()) {
    return;
  }

  // Return existing promise if already initializing
  if (initializationPromise) {
    return initializationPromise;
  }

  // Skip if already loaded
  if (isScriptLoaded && isGtagAvailable()) {
    return;
  }

  // Skip if already loading
  if (isScriptLoading) {
    return;
  }

  isScriptLoading = true;

  initializationPromise = (async () => {
    try {
      await loadGtagScript();

      // Configure GA4 with measurement ID
      const gtag = window.gtag;
      if (gtag) {
        gtag('config', config.analytics.measurementId, {
          send_page_view: false, // We'll handle page views manually for SPA
          anonymize_ip: config.analytics.anonymizeIp,
        });
      }
    } catch (error) {
      // Log error but don't throw - analytics should fail silently
      if (config.analytics.debug) {
        console.error('[GA4] Initialization failed:', error);
      }
    }
  })();

  return initializationPromise;
};

/**
 * Track a page view
 *
 * @param params - Page view parameters
 * @param params.path - The page path (e.g., '/dashboard')
 * @param params.title - Optional page title
 * @param params.location - Optional full URL (defaults to current location)
 */
export const trackPageView = ({ path, title, location }: PageViewParams): void => {
  if (!shouldEnableAnalytics() || !isGtagAvailable()) {
    return;
  }

  const gtag = window.gtag;
  if (!gtag) {
    return;
  }

  const pageViewParams: Record<string, unknown> = {
    page_path: sanitizePageUrl(path),
    page_location: location ? sanitizePageUrl(location) : getDefaultPageLocation(),
  };

  if (title) {
    pageViewParams.page_title = title;
  }

  gtag('event', 'page_view', pageViewParams);
};

/**
 * Track a custom event
 *
 * @param eventName - The name of the event (e.g., 'button_click', 'form_submit')
 * @param params - Optional event parameters
 */
export const trackEvent = (eventName: string, params?: EventParams): void => {
  if (!shouldEnableAnalytics() || !isGtagAvailable()) {
    return;
  }

  const gtag = window.gtag;
  if (!gtag) {
    return;
  }

  const eventParams: Record<string, unknown> = { ...params };

  // Map common params to GA4 format
  if (params?.category) {
    eventParams.event_category = params.category;
    delete eventParams.category;
  }
  if (params?.label) {
    eventParams.event_label = params.label;
    delete eventParams.label;
  }

  gtag('event', eventName, eventParams);
};

/**
 * Set user properties for analytics
 * Useful for segmentation and analysis
 *
 * @param properties - User properties to set (do not include PII)
 */
export const setUserProperties = (properties: Record<string, unknown>): void => {
  if (!shouldEnableAnalytics() || !isGtagAvailable()) {
    return;
  }

  const gtag = window.gtag;
  if (!gtag) {
    return;
  }

  // Validate no PII is being sent
  const piiFields = ['email', 'name', 'phone', 'address', 'ssn', 'password'];
  const hasPII = Object.keys(properties).some((key) =>
    piiFields.some((pii) => key.toLowerCase().includes(pii))
  );

  if (hasPII) {
    return;
  }

  gtag('set', { user_properties: properties });
};

/**
 * Update consent state for GDPR compliance
 *
 * @param consentGranted - Whether the user has granted consent
 */
export const updateConsent = (consentGranted: boolean): void => {
  if (!isGtagAvailable()) {
    return;
  }

  const gtag = window.gtag;
  if (!gtag) {
    return;
  }

  gtag('consent', 'update', {
    analytics_storage: consentGranted ? 'granted' : 'denied',
  });
};

/**
 * Get the current analytics configuration status
 * Useful for debugging and UI display
 */
export const getAnalyticsStatus = (): {
  enabled: boolean;
  measurementId: string;
  debugMode: boolean;
  gtagLoaded: boolean;
} => ({
  enabled: shouldEnableAnalytics(),
  measurementId: config.analytics.measurementId || 'not set',
  debugMode: config.analytics.debug,
  gtagLoaded: isGtagAvailable(),
});
